// src/adminDashboard/shared/components/DataTable/DataTable.tsx
// Reusable data table component with advanced features

import {
    ChevronUp,
    ChevronDown,
    ChevronsUpDown,
    ChevronsLeft,
    ChevronsRight,
    ChevronLeft,
    ChevronRight,
    Download,
    LucideIcon
} from 'lucide-react';
import React, { useState, useMemo } from 'react';

import EmptyState from '../../../../shared/components/EmptyState';
import { useIsMobile } from '../../hooks/useMediaQuery';

import BulkActionsToolbar, { BulkAction } from './BulkActionsToolbar';
import ColumnVisibilityMenu from './ColumnVisibilityMenu';
import MobileCard from './MobileCard';
import { exportToCSV, exportToExcel, ColumnDef } from './utils/exportHelpers';

export type { BulkAction, ColumnDef };

type BivariantHandler<T extends (...args: never[]) => unknown> = {
    bivarianceHack(...args: Parameters<T>): ReturnType<T>;
}['bivarianceHack'];
const toRecord = (row: object): Record<string, unknown> => row as Record<string, unknown>;

interface PaginationConfig {
    page: number;
    perPage: number;
    total: number;
    onPageChange: (page: number) => void;
    onPerPageChange?: ((perPage: number) => void) | undefined;
}

interface DataTableProps<T extends object = object> {
    columns: ColumnDef[];
    data?: T[] | undefined;
    isLoading?: boolean | undefined;
    emptyMessage?: string | undefined;
    emptyIcon?: LucideIcon | undefined;
    sortable?: boolean | undefined;
    pagination?: PaginationConfig | null | undefined;
    onRowClick?: BivariantHandler<(row: T) => void> | undefined;
    rowActions?: BivariantHandler<(row: T) => React.ReactNode> | undefined;
    selectedRows?: (string | number)[] | undefined;
    onSelectRow?: ((rowId: string | number) => void) | undefined;
    onSelectAll?: ((selected: boolean) => void) | undefined;
    getId?: BivariantHandler<(row: T) => string | number> | undefined;
    exportable?: boolean | undefined;
    exportFilename?: string | undefined;
    bulkActions?: BulkAction[] | undefined;
    hiddenColumns?: string[] | undefined;
    onToggleColumn?: ((key: string) => void) | undefined;
    expandedRowId?: string | number | null | undefined;
    expandedRowContent?: BivariantHandler<(row: T) => React.ReactNode> | undefined;
}

const DataTable = <T extends object = object,>({
    columns,
    data = [],
    isLoading = false,
    emptyMessage = 'No data found',
    emptyIcon,
    sortable = true,
    pagination = null,
    onRowClick,
    rowActions,
    selectedRows = [],
    onSelectRow,
    onSelectAll,
    getId = (row: T) => {
        const rowId = toRecord(row).id;
        if (typeof rowId === 'string' || typeof rowId === 'number') {
            return rowId;
        }
        return '';
    },
    exportable = false,
    exportFilename = 'export',
    bulkActions = [],
    hiddenColumns = [],
    onToggleColumn,
    expandedRowId,
    expandedRowContent,
}: DataTableProps<T>) => {
    const [sortConfig, setSortConfig] = useState<{ key: string | null; direction: 'asc' | 'desc' }>({ key: null, direction: 'asc' });
    const isMobile = useIsMobile();
    const resolvePositiveInt = (value: unknown, fallback: number): number => {
        const numeric = Number(value);
        if (!Number.isFinite(numeric) || numeric <= 0) {
            return fallback;
        }
        return Math.floor(numeric);
    };

    // Filter visible columns
    const visibleColumns = useMemo(() => {
        return columns.filter(col => !hiddenColumns.includes(col.key));
    }, [columns, hiddenColumns]);

    // Handle sorting
    const handleSort = (key: string) => {
        if (!sortable) return;

        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    // Sort data
    const sortedData = useMemo(() => {
        const sortKey = sortConfig.key;
        if (!sortKey) return data;

        const normalizeSortValue = (value: unknown): string | number | null => {
            if (value === null || value === undefined) return null;
            if (typeof value === 'number') return value;
            if (value instanceof Date) return value.getTime();
            return String(value).toLowerCase();
        };

        return [...data].sort((a, b) => {
            const column = columns.find((col) => col.key === sortKey);
            const aRaw = column?.getValue ? column.getValue(a) : toRecord(a)[sortKey];
            const bRaw = column?.getValue ? column.getValue(b) : toRecord(b)[sortKey];
            const aValue = normalizeSortValue(aRaw);
            const bValue = normalizeSortValue(bRaw);

            if (aValue === bValue) return 0;
            if (aValue === null) return 1;
            if (bValue === null) return -1;

            const comparison = aValue < bValue ? -1 : 1;
            return sortConfig.direction === 'asc' ? comparison : -comparison;
        });
    }, [data, sortConfig, columns]);

    // Export handlers
    const handleExportCSV = () => {
        exportToCSV(sortedData, visibleColumns, exportFilename);
    };

    const handleExportExcel = () => {
        exportToExcel(sortedData, visibleColumns, exportFilename);
    };

    // Render sort icon
    const renderSortIcon = (key: string) => {
        if (sortConfig.key !== key) {
            return <ChevronsUpDown size={14} className="text-slate-400" />;
        }
        return sortConfig.direction === 'asc' ? (
            <ChevronUp size={14} className="text-primary" />
        ) : (
            <ChevronDown size={14} className="text-primary" />
        );
    };

    // Loading skeleton
    if (isLoading) {
        return (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200">
                        <thead className="bg-slate-50">
                            <tr>
                                {visibleColumns.map((col) => (
                                    <th
                                        key={col.key}
                                        className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider"
                                    >
                                        <div className="h-4 w-20 bg-slate-200 rounded animate-pulse" />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                            {[...Array(5)].map((_, i) => (
                                <tr key={i}>
                                    {visibleColumns.map((col) => (
                                        <td key={col.key} className="px-4 py-3.5">
                                            <div className="h-4 bg-slate-100 rounded animate-pulse" />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    // Empty state
    if (data.length === 0) {
        return (
            <div className="bg-white rounded-xl border border-slate-200 p-8">
                <EmptyState message={emptyMessage} icon={emptyIcon} />
            </div>
        );
    }

    const fallbackPerPage = resolvePositiveInt(data.length, 1);
    const backendTotal = pagination ? Math.max(0, Number(pagination.total) || 0) : data.length;
    const pageSize = pagination ? resolvePositiveInt(pagination.perPage, fallbackPerPage) : fallbackPerPage;
    const totalPages = Math.max(1, Math.ceil(backendTotal / pageSize));
    const currentPage = pagination
        ? Math.min(Math.max(resolvePositiveInt(pagination.page, 1), 1), totalPages)
        : 1;
    const startItem = backendTotal === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endItem = backendTotal === 0 ? 0 : Math.min(currentPage * pageSize, backendTotal);
    const hasSelection = selectedRows.length > 0;

    return (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {/* Bulk Actions Toolbar */}
            {hasSelection && bulkActions.length > 0 && (
                <BulkActionsToolbar
                    selectedCount={selectedRows.length}
                    onClearSelection={() => onSelectAll?.(false)}
                    actions={bulkActions.map(action => ({
                        ...action,
                        onClick: () => action.onClick(selectedRows)
                    }))}
                />
            )}

            {/* Export and Column Visibility Controls */}
            {(exportable || onToggleColumn) && (
                <div className="flex items-center justify-end gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50">
                    {onToggleColumn && (
                        <ColumnVisibilityMenu
                            columns={columns}
                            hiddenColumns={hiddenColumns}
                            onToggle={onToggleColumn}
                        />
                    )}

                    {exportable && (
                        <div className="flex items-center gap-1">
                            <button
                                onClick={handleExportCSV}
                                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-white rounded-lg transition-colors"
                                title="Export to CSV"
                            >
                                <Download size={14} />
                                CSV
                            </button>
                            <button
                                onClick={handleExportExcel}
                                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-white rounded-lg transition-colors"
                                title="Export to Excel"
                            >
                                <Download size={14} />
                                Excel
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Mobile View - Card Layout */}
            {isMobile ? (
                <div className="p-4 space-y-3">
                    {sortedData.map((row, rowIndex) => {
                        const rowId = getId(row);
                        const isSelected = selectedRows.includes(rowId);

                        return (
                            <MobileCard
                                key={rowId || rowIndex}
                                row={row}
                                columns={visibleColumns}
                                onRowClick={onRowClick}
                                rowActions={rowActions}
                                isSelected={isSelected}
                                onSelectRow={onSelectRow}
                                getId={getId}
                            />
                        );
                    })}
                </div>
            ) : (
                /* Desktop View - Table Layout */
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200">
                        <thead className="bg-slate-50">
                            <tr>
                                {/* Selection checkbox */}
                                {onSelectAll && (
                                    <th className="px-4 py-3 w-10">
                                        <input
                                            type="checkbox"
                                            checked={selectedRows.length === data.length && data.length > 0}
                                            onChange={(e) => onSelectAll(e.target.checked)}
                                            className="rounded border-slate-300 text-primary focus:ring-primary"
                                        />
                                    </th>
                                )}

                                {visibleColumns.map((col) => (
                                    <th
                                        key={col.key}
                                        className={`
                    px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider
                    ${col.sortable !== false && sortable ? 'cursor-pointer hover:bg-slate-100' : ''}
                    ${col.align === 'right' ? 'text-right' : ''}
                    ${col.align === 'center' ? 'text-center' : ''}
                  `}
                                        onClick={() => col.sortable !== false && handleSort(col.key)}
                                        style={{ width: col.width }}
                                    >
                                        <div className={`flex items-center gap-1.5 ${col.align === 'right' ? 'justify-end' : ''}`}>
                                            {col.header}
                                            {col.sortable !== false && sortable && renderSortIcon(col.key)}
                                        </div>
                                    </th>
                                ))}

                                {/* Actions column */}
                                {rowActions && (
                                    <th className="px-4 py-3 w-16 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                )}
                            </tr>
                        </thead>

                        <tbody className="divide-y divide-slate-200 bg-white">
                            {sortedData.map((row, rowIndex) => {
                                const rowId = getId(row);
                                const isSelected = selectedRows.includes(rowId);

                                return (
                                    <React.Fragment key={rowId || rowIndex}>
                                        <tr
                                            className={`
                    ${onRowClick ? 'cursor-pointer hover:bg-slate-50' : ''}
                    ${isSelected ? 'bg-primary/5' : ''}
                    transition-colors
                  `}
                                            onClick={() => onRowClick?.(row)}
                                        >
                                            {/* Selection checkbox */}
                                            {onSelectRow && (
                                                <td className="px-4 py-3.5" onClick={(e) => e.stopPropagation()}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => onSelectRow(rowId)}
                                                        className="rounded border-slate-300 text-primary focus:ring-primary"
                                                    />
                                                </td>
                                            )}

                                            {visibleColumns.map((col) => (
                                                <td
                                                    key={col.key}
                                                    className={`
                        px-4 py-3.5 text-sm text-slate-700
                        ${col.align === 'right' ? 'text-right' : ''}
                        ${col.align === 'center' ? 'text-center' : ''}
                      `}
                                                >
                                                    {col.render ? col.render(row, rowIndex) : (
                                                        typeof toRecord(row)[col.key] === 'object'
                                                            ? JSON.stringify(toRecord(row)[col.key] ?? '')
                                                            : String(toRecord(row)[col.key] ?? '')
                                                    )}
                                                </td>
                                            ))}

                                            {/* Row actions */}
                                            {rowActions && (
                                                <td className="px-4 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                                                    {rowActions(row)}
                                                </td>
                                            )}
                                        </tr>

                                        {/* Expanded Content */}
                                        {
                                            expandedRowId === rowId && expandedRowContent && (
                                                <tr key={`${rowId}-expanded`} className="bg-slate-50/50">
                                                    <td colSpan={visibleColumns.length + (onSelectRow ? 1 : 0) + (rowActions ? 1 : 0)} className="px-4 py-3 border-t border-slate-100">
                                                        {expandedRowContent(row)}
                                                    </td>
                                                </tr>
                                            )
                                        }
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )
            }

            {/* Pagination */}
            {
                pagination && (
                    <div className="flex flex-col sm:flex-row gap-4 items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
                        <div className="flex items-center gap-4">
                            <div className="text-sm text-slate-500 text-center sm:text-left">
                                Showing{' '}
                                <span className="font-medium">
                                    {startItem}
                                </span>{' '}
                                to{' '}
                                <span className="font-medium">
                                    {endItem}
                                </span>{' '}
                                of <span className="font-medium">{backendTotal}</span> results
                            </div>

                            {/* Per-page selector */}
                            {pagination.onPerPageChange && (
                                <div className="flex items-center gap-2">
                                    <label className="text-sm text-slate-500">Per page:</label>
                                    <select
                                        value={pagination.perPage}
                                        onChange={(e) => {
                                            const onPerPageChange = pagination.onPerPageChange;
                                            if (onPerPageChange) {
                                                onPerPageChange(Number(e.target.value));
                                            }
                                        }}
                                        className="px-2 py-1 border border-slate-200 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
                                    >
                                        <option value="10">10</option>
                                        <option value="20">20</option>
                                        <option value="50">50</option>
                                        <option value="100">100</option>
                                    </select>
                                </div>
                            )}
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => pagination.onPageChange(1)}
                                disabled={currentPage <= 1}
                                className="p-2 rounded-lg border border-slate-200 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                                title="First page"
                            >
                                <ChevronsLeft size={16} />
                            </button>
                            <button
                                onClick={() => pagination.onPageChange(Math.max(1, currentPage - 1))}
                                disabled={currentPage <= 1}
                                className="p-2 rounded-lg border border-slate-200 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Previous page"
                            >
                                <ChevronLeft size={16} />
                            </button>

                            <span className="text-sm text-slate-600">
                                Page {currentPage} of {totalPages}
                            </span>

                            <button
                                onClick={() => pagination.onPageChange(Math.min(totalPages, currentPage + 1))}
                                disabled={currentPage >= totalPages}
                                className="p-2 rounded-lg border border-slate-200 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Next page"
                            >
                                <ChevronRight size={16} />
                            </button>
                            <button
                                onClick={() => pagination.onPageChange(totalPages)}
                                disabled={currentPage >= totalPages}
                                className="p-2 rounded-lg border border-slate-200 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Last page"
                            >
                                <ChevronsRight size={16} />
                            </button>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default DataTable;
